import type { FC } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { imgSize400 } from 'constants/image';
import { formatImagelink } from 'utils';
import { DateUtils } from 'utils/DateUtils';

import { CurrencyEnum, OfferStateEnum } from '@Modules/offers/constants';
import { getOfferImage } from '@Modules/offers/utils';
import { OfferConditionEnum, OfferGenderEnum, OfferTypeEnum } from '@Modules/offers/types';
import { selectLanguage, selectRegion } from '@Modules/app/selectors';
import { selectOfferMarkup } from '@Modules/offers/selectors';
import type { OfferCardType } from '@Modules/offers/types';

type SchemaProps = {
  offerCard: OfferCardType | null;
};

export const Schema: FC<SchemaProps> = ({ offerCard }) => {
  const region = useSelector(selectRegion);
  const offerMarkup = useSelector(selectOfferMarkup);
  const language = useSelector(selectLanguage);
  let offerSchema = null;

  if (!offerCard?.card || !region) {
    return null;
  }

  const offer = offerCard.card;

  const image =
    offer.primaryPhoto?.upload.cropUrlTemplate || offer.photos[0]?.upload.cropUrlTemplate;

  if (offerMarkup?.type === OfferTypeEnum.GOODS) {
    offerSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      sku: offer.id,
      name: offer.title,
      description: offer.description.slice(0, 400),
      // Offers without photos have no image: formatImagelink would crash SSR on undefined
      ...(image && { image: [formatImagelink(image, imgSize400)] }),
      ...(offerMarkup.gender && {
        gender:
          offerMarkup.gender === OfferGenderEnum.MALE
            ? 'https://schema.org/Male'
            : 'https://schema.org/Female',
      }),
      brand: {
        '@type': 'Brand',
        name: offerMarkup.brand,
      },
      offers: {
        '@type': 'Offer',
        url: `https://birbir.uz/${offer.webUriInfo[language]}`,
        priceCurrency: offer.price?.currency || CurrencyEnum.UZS,
        price: (offer.price?.value || 0) / 100,
        itemCondition:
          offerMarkup.condition === OfferConditionEnum.NEW
            ? 'https://schema.org/NewCondition'
            : 'https://schema.org/UsedCondition',
        availability:
          offerCard.state === OfferStateEnum.PUBLISHED
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        seller: {
          '@type': offer.seller.business ? 'Organization' : 'Person',
          name: offer.seller.name,
        },
        availableAtOrFrom: {
          '@type': 'Place',
          name: region.title,
          address: {
            '@type': 'PostalAddress',
            addressLocality: region.title,
            addressCountry: 'UZ',
          },
        },
        validFrom: DateUtils.format(offer.publishedAt, 'YYYY-MM-DD'),
        areaServed: {
          '@type': 'City',
          name: offer.region.title,
        },
      },
      additionalProperty: offerMarkup.additionalProperties.map((property) => ({
        '@type': 'PropertyValue',
        name: property.key,
        value: property.values.join(', '),
      })),
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org/',
          '@type': 'ImageObject',
          contentLocation: offer.region.title,
          contentUrl: getOfferImage(offer),
          name: offer.title,
        })}
      </script>

      {offerSchema && <script type="application/ld+json">{JSON.stringify(offerSchema)}</script>}
    </Helmet>
  );
};
